import { ref, computed, watch } from 'vue';
import useUpc from '~/modules/upc/composables/useUpc';
import { useBrand } from '~/composables/useBrand';
import { useProduct } from '~/modules/product/composables/useProduct';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import useAvailabilityStore from '~/stores/useAvailabilityStore';
import { useFulfilmentData } from './useFulfilmentData';
import useGetItTodayExperiments from './useGetItTodayExperiments';
import { pickupStoreParser } from '../utils/pickupStoreParser';
import { productInfoParser } from '../utils/productInfoParser';
import { useProductType } from '~/composables/useProductType';
import useSelectedMemberId from '~/modules/latchkey/composables/useSelectedMemberId';
import { getMemberProduct } from '~/utils/product';

/**
 * @typedef {{
 * availabilityDataParam?: import('../types').bopsAvailabilityXapiResponce,
 * selectedUpcIdParam?: string,
 * selectedRadioButtonParam?: string,
 * isSiblingUpcParam?: boolean,
 * }} parseParams
 */

/**
 * @returns {{
 * parseAvailabilityData: (param?: parseParams) => void,
 * resetShippingOptionUpdateStatus: () => void,
 * isAvailabilityEnabled: import('vue').ComputedRef<boolean>,
 * pickupStore: import('vue').Ref<import('../types').PickupStore | {}>,
 * productInfo: import('vue').Ref<import('../types').ProductInfo>,
 * isZipCodeUpdated: import('vue').ComputedRef<boolean>,
 * isStoreUpdated: import('vue').ComputedRef<boolean>
 * }}
 */
export const useAvailability = () => {
  const { isMcom } = useBrand();
  const { productData: product } = useProduct();
  const selectedMemberId = useSelectedMemberId();
  const { isInternationalMode, isShopTheSetExpEnabled } = useFeatureEligibility();
  const { isShopTheSet: isShopTheSetType } = useProductType();
  const isShopTheSet = isShopTheSetExpEnabled && isShopTheSetType;
  const { isCountryEligibleFlag, isBigTicketItemFlag } = useProductFlags();
  const { selectedUpcId, selectedUpcNumber, upcData, isSiblingUpc } = useUpc();
  const { killswitches } = useKillswitches();
  const { isChipFulfillmentEnabled } = useGetItTodayExperiments();
  const isFulfillmentDrivenAvailabilityEnabled = computed(() =>
    Boolean(killswitches.isFulfillmentDrivenAvailabilityEnabled && isChipFulfillmentEnabled.value),
  );

  const { zipCode, storeLocation, selectedRadioButton } = useFulfilmentData();
  const showImmediately = ref(!!selectedUpcNumber.value);
  const isAvailabilityEnabledMcom = computed(() => showImmediately.value && isCountryEligibleFlag && !isInternationalMode && !isBigTicketItemFlag);
  const isAvailabilityEnabledBcom = computed(() => showImmediately.value && isCountryEligibleFlag && !isBigTicketItemFlag);
  const isAvailabilityEnabled = computed(() => (isMcom ? isAvailabilityEnabledMcom.value : isAvailabilityEnabledBcom.value));
  const availabilityData = computed(() => useAvailabilityStore().data);
  const availabilityStore = useAvailabilityStore();
  const zipCodeFromStore = computed(() => availabilityStore.zipCodeFromStore);
  const isZipCodeUpdated = computed(() => availabilityStore.isZipCodeUpdated);
  const isStoreUpdated = computed(() => availabilityStore.isStoreUpdated);

  const pickupStore = ref({});
  const productInfo = ref(
    productInfoParser({
      selectedRadioButton: selectedRadioButton.value,
      availabilityData: availabilityData.value,
      selectedUpcId: selectedUpcId.value,
      upcData: upcData.value,
      product: isShopTheSet ? getMemberProduct(product, selectedMemberId.value) : product,
      zipCode: zipCode.value,
      isBopsUpcAvailabilityUpdateEnabled: killswitches.isBopsUpcAvailabilityUpdateEnabled,
      isFulfillmentDrivenAvailabilityEnabled: isFulfillmentDrivenAvailabilityEnabled.value,
      isSiblingUpc: Boolean(isSiblingUpc?.value),
    }),
  );

  function parseAvailabilityData({
    availabilityDataParam = availabilityData.value,
    selectedUpcIdParam = selectedUpcId.value,
    selectedRadioButtonParam = selectedRadioButton.value,
    isSiblingUpcParam = Boolean(isSiblingUpc?.value),
  } = {}) {
    pickupStore.value = pickupStoreParser({
      availabilityData: availabilityDataParam,
      storeId: storeLocation.value,
    });

    productInfo.value = productInfoParser({
      selectedRadioButton: selectedRadioButtonParam,
      availabilityData: availabilityDataParam,
      selectedUpcId: selectedUpcIdParam,
      upcData: upcData.value,
      product: isShopTheSet ? getMemberProduct(product, selectedMemberId.value) : product,
      zipCode: zipCode.value,
      isBopsUpcAvailabilityUpdateEnabled: killswitches.isBopsUpcAvailabilityUpdateEnabled,
      isFulfillmentDrivenAvailabilityEnabled: isFulfillmentDrivenAvailabilityEnabled.value,
      isSiblingUpc: isSiblingUpcParam,
    });
  }

  function resetShippingOptionUpdateStatus() {
    availabilityStore.setShippingOptionUpdateStatus({ isZipCodeUpdated: false, isStoreUpdated: false });
  }

  watch(
    [selectedUpcId, availabilityData, selectedRadioButton, () => isSiblingUpc?.value],
    ([newSelectedUpcId, newAvailabilityData, newSelectedRadioButton, newIsSiblingUpc]) => {
      parseAvailabilityData({
        selectedUpcIdParam: newSelectedUpcId,
        availabilityDataParam: newAvailabilityData,
        selectedRadioButtonParam: newSelectedRadioButton,
        isSiblingUpcParam: Boolean(newIsSiblingUpc),
      });
    },
  );

  watch(selectedMemberId, () => {
    if (isShopTheSetExpEnabled && !selectedUpcId.value) {
      showImmediately.value = false;
    }
  });

  watch(
    selectedUpcId,
    () => {
      showImmediately.value = true;
    },
    { once: isShopTheSetExpEnabled ? false : true },
  );

  watch(
    zipCodeFromStore,
    (newValue) => {
      if (!zipCode.value) {
        zipCode.value = newValue;
      }
    },
    { once: true },
  );

  return {
    parseAvailabilityData,
    resetShippingOptionUpdateStatus,
    isAvailabilityEnabled,
    pickupStore,
    productInfo,
    isZipCodeUpdated,
    isStoreUpdated,
  };
};

export default useAvailability;
